import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";

// Aparece quando a conta ainda não confirmou o e-mail (cadastro novo ou tentativa de login).
// O código de 6 dígitos já foi enviado pelo servidor; aqui a pessoa digita, ou pede outro (1 por minuto).
export default function EmailVerifyModal({ ctx, onClose, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(ctx?.autoSent ? 60 : 0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  if (!ctx) return null;
  const creds = { username: ctx.username, password: ctx.password };

  const confirm = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) { toast.error("Digite os 6 dígitos do código."); return; }
    setBusy(true);
    try {
      await api.post("/auth/email/verify", { ...creds, code: code.trim() });
      toast.success("E-mail confirmado!");
      await onVerified();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível confirmar o código.");
      setCode("");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/email/send-code", creds);
      toast.success(data?.message || "Código enviado.");
      setWait(60);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível enviar o código agora.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="email-verify-modal">
      <form onSubmit={confirm} className="relative w-full max-w-[420px] rounded-2xl border border-white/10 bg-[#0b0b10] p-8 space-y-6 shadow-[0_0_60px_rgba(0,0,0,0.6)]">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" aria-label="Fechar"><X size={18} /></button>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-2xl bg-[var(--color-main)]/10 border border-[var(--color-main)]/30 flex items-center justify-center"><Mail size={22} className="text-[var(--color-main)]" /></div>
          <h2 className="text-2xl font-black uppercase italic tracking-[3px] text-white">Confirme seu e-mail</h2>
          <p className="text-sm text-white/60">
            Enviamos um código de 6 dígitos para <b className="text-white">{ctx.emailHint || "o seu e-mail"}</b>. Ele vale por 15 minutos. Olhe também a caixa de spam.
          </p>
        </div>
        <input
          autoFocus inputMode="numeric" maxLength={6} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="000000" data-testid="email-code-input"
          className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-center text-3xl font-black tracking-[12px] text-white focus:outline-none focus:border-[var(--color-main)]/60"
        />
        <button type="submit" disabled={busy} className="w-full rounded-xl bg-[var(--color-main)] py-3 text-sm font-black uppercase tracking-[3px] text-white disabled:opacity-60">
          {busy ? "Confirmando..." : "Confirmar e entrar"}
        </button>
        <button type="button" disabled={busy || wait > 0} onClick={resend} className="w-full text-xs font-bold uppercase tracking-[2px] text-white/50 hover:text-white disabled:opacity-40" data-testid="email-resend">
          {wait > 0 ? `Pedir outro código em ${wait}s` : "Não recebi: enviar outro código"}
        </button>
      </form>
    </div>
  );
}
